import { useState } from 'react'
import { Plus, Search, MessageSquare, Clock } from 'lucide-react'
import type { QaThreadSummary } from '../../lib/api'

interface QaThreadListProps {
  threads: QaThreadSummary[]
  selectedId: string | null
  onSelect: (id: string) => void
  onNew: () => void
  loading?: boolean
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400',
  extracted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  complete: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  reviewed: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
  sent: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400',
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function QaThreadList({ threads, selectedId, onSelect, onNew, loading }: QaThreadListProps) {
  const [search, setSearch] = useState('')

  const filtered = threads.filter((t) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      (t.title || '').toLowerCase().includes(q) ||
      (t.sender_name || '').toLowerCase().includes(q) ||
      (t.sender_email || '').toLowerCase().includes(q)
    )
  })

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-white/10">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Threads</h3>
          <button
            onClick={onNew}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            New
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search threads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors"
          />
        </div>
      </div>

      {/* Thread list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center">
            <MessageSquare className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {search ? 'No threads match your search' : 'No threads yet'}
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filtered.map((thread) => {
              const isSelected = thread.id === selectedId
              return (
                <button
                  key={thread.id}
                  onClick={() => onSelect(thread.id)}
                  className={`w-full text-left p-2.5 rounded-lg transition-all duration-150 ${
                    isSelected
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-300 dark:border-indigo-500/50'
                      : 'hover:bg-gray-50 dark:hover:bg-white/5 border border-transparent'
                  }`}
                >
                  {/* Title */}
                  <p className={`text-xs font-medium truncate ${
                    isSelected
                      ? 'text-indigo-900 dark:text-indigo-200'
                      : 'text-gray-900 dark:text-gray-200'
                  }`}>
                    {thread.title || 'Untitled Thread'}
                  </p>

                  {/* Sender */}
                  {thread.sender_name && (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {thread.sender_name}
                    </p>
                  )}

                  {/* Bottom row: badges and date */}
                  <div className="flex items-center gap-1.5 mt-1.5">
                    {/* Question count */}
                    {thread.question_count > 0 && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 rounded">
                        {thread.question_count} Q
                      </span>
                    )}

                    {/* Status badge */}
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${STATUS_STYLES[thread.status] || STATUS_STYLES.pending}`}>
                      {thread.status}
                    </span>

                    {/* Date */}
                    <span className="ml-auto flex items-center gap-0.5 text-[10px] text-gray-400 dark:text-gray-500">
                      <Clock className="w-2.5 h-2.5" />
                      {formatRelativeDate(thread.created_at)}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
